import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  House,
  KeyRound,
  Mail,
  Menu,
  Phone,
  Search,
  X,
} from "lucide-react";
import Brand from "@/components/common/Brand";
import Neighborhood from "@/components/public/Neighborhood";
import { publicListings } from "@/data/publicListings";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const EMAIL = "highlanderhomes22@gmail.com";
const formatRent = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
const faqs = [
  [
    "How do I find out what’s available?",
    "Ask us about current openings using the inquiry form below. We’ll confirm availability, rent, and property details directly before you make plans.",
  ],
  [
    "Can I schedule a tour?",
    "Yes. Tell us which home you’re interested in and when you’d like to move. We’ll get in touch about availability and arranging a tour.",
  ],
  [
    "Are pets allowed?",
    "Pet policies can vary by property. Include your pet details in your inquiry and we’ll confirm the policy for the home you’re considering.",
  ],
  [
    "I’m a current resident. How do I get help?",
    "Call 240-449-4338 or email highlanderhomes22@gmail.com with your address and a description of the issue. For immediate danger or an emergency, call 911 first.",
  ],
];

export default function Index() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [availability, setAvailability] = useState("all");
  const [selected, setSelected] = useState(null);
  const [inquiry, setInquiry] = useState({
    name: "",
    email: "",
    move: "",
    message: "",
  });
  const [emailReady, setEmailReady] = useState(false);
  const listings = useMemo(
    () =>
      publicListings.filter(
        (home) =>
          `${home.name} ${home.area}`
            .toLowerCase()
            .includes(query.toLowerCase()) &&
          (availability === "all" || home.status === availability),
      ),
    [query, availability],
  );
  const draftUrl = `mailto:${EMAIL}?subject=${encodeURIComponent("Rental inquiry — Highlander Homes")}&body=${encodeURIComponent(`Name: ${inquiry.name}\nEmail: ${inquiry.email}\nPreferred move-in: ${inquiry.move || "Flexible"}\n\n${inquiry.message}`)}`;
  return (
    <div className="rental-site">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="public-header">
        <Brand />
        <nav className="public-nav" aria-label="Main navigation">
          <a href="#homes">Our homes</a>
          <a href="#approach">Our approach</a>
          <a href="#questions">Good to know</a>
        </nav>
        <Link to="/login" className="owner-link">
          <KeyRound size={15} /> Owner sign in
        </Link>
        <button
          className="mobile-public-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>
      {menuOpen && (
        <nav className="public-mobile-nav" aria-label="Mobile navigation">
          {[
            ["#homes", "Our homes"],
            ["#approach", "Our approach"],
            ["#questions", "Good to know"],
            ["#contact", "Get in touch"],
          ].map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)}>
              {label}
              <ArrowRight size={16} />
            </a>
          ))}
        </nav>
      )}
      <main id="main-content">
        <section className="public-hero">
          <div className="hero-copy">
            <p className="eyebrow">
              <span /> A SMALL COLLECTION. A PERSONAL APPROACH.
            </p>
            <h1>
              A place to live.
              <br />
              <em>Room to belong.</em>
            </h1>
            <p className="hero-description">
              Five rental homes. One direct connection.
              <br />
              Find your next chapter with Highlander Homes.
            </p>
            <div className="hero-actions">
              <a className="public-button" href="#homes">
                Explore our homes <ArrowRight size={18} />
              </a>
              <a className="text-link" href="#contact">
                Let’s talk <ArrowUpRight size={17} />
              </a>
            </div>
            <div className="hero-note">
              <span className="little-house">
                <House size={20} />
              </span>
              <p>
                A home is personal.
                <br />
                <strong>Renting one should feel that way, too.</strong>
              </p>
            </div>
          </div>
          <div className="hero-art">
            <Neighborhood />
            <div className="art-label">
              <span>HIGHLANDER HOMES</span>
              <span>Make yourself at home. ↗</span>
            </div>
          </div>
        </section>
        <div className="public-values">
          <span>
            <House size={19} /> Five homes, thoughtfully managed
          </span>
          <span>
            <Phone size={18} /> A real person to contact
          </span>
          <span>
            <KeyRound size={19} /> Your next chapter starts here
          </span>
        </div>
        <section id="homes" className="public-section homes-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">FIND YOUR PLACE</p>
              <h2>A home for what’s next.</h2>
            </div>
            <p>
              Looking for a little more room?
              <br />
              Let’s find out what’s available.
            </p>
          </div>
          {publicListings.length > 0 ? (
            <>
              <div className="rental-filters">
                <label>
                  <Search size={18} />
                  <input
                    aria-label="Search homes"
                    placeholder="Search homes or neighborhoods"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </label>
                <select
                  aria-label="Availability"
                  value={availability}
                  onChange={(e) => setAvailability(e.target.value)}
                >
                  <option value="all">All homes</option>
                  <option value="available">Available now</option>
                  <option value="coming-soon">Coming soon</option>
                  <option value="leased">Leased</option>
                </select>
              </div>
              <p className="listing-count" aria-live="polite">
                {listings.length} {listings.length === 1 ? "home" : "homes"}
              </p>
              <div className="rental-grid">
                {listings.map((home) => (
                  <article className="rental-card" key={home.id}>
                    {home.image && (
                      <img
                        src={home.image}
                        alt={home.imageAlt || home.name}
                        loading="lazy"
                      />
                    )}
                    <div>
                      <p className="eyebrow">
                        {home.status.replaceAll("-", " ")}
                      </p>
                      <h3>{home.name}</h3>
                      <p>
                        {[
                          home.area,
                          home.bedrooms && `${home.bedrooms} bed`,
                          home.bathrooms && `${home.bathrooms} bath`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <strong>{formatRent(home.rent)} / month</strong>
                      <button
                        className="text-link"
                        onClick={() => setSelected(home)}
                      >
                        View home <ArrowUpRight size={16} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              {listings.length === 0 && (
                <div className="availability-card">
                  <p>No homes match those filters.</p>
                  <button
                    className="text-link"
                    onClick={() => {
                      setQuery("");
                      setAvailability("all");
                    }}
                  >
                    Clear filters <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="availability-card">
              <div className="availability-icon">
                <KeyRound size={30} strokeWidth={1.3} />
              </div>
              <div>
                <h3>Your next home could be here.</h3>
                <p>
                  Contact us for current availability, photos, pricing, and tour
                  options.
                </p>
                <span>
                  Online listings are being updated. Availability is confirmed
                  directly.
                </span>
              </div>
              <a href="#contact" className="public-button">
                Ask about availability <ArrowUpRight size={18} />
              </a>
            </div>
          )}
        </section>
        <section id="approach" className="approach-section">
          <div>
            <p className="eyebrow">SMALL BY DESIGN</p>
            <h2>
              Homes, not just
              <br />
              <em>addresses.</em>
            </h2>
            <p>
              With a collection of five houses, we keep things personal. From
              your first question to your next move, you can connect directly
              with Highlander Homes.
            </p>
          </div>
          <div className="approach-points">
            {[
              [
                "01",
                "A direct conversation",
                "Questions about a home? Talk to the people who manage it.",
              ],
              [
                "02",
                "Know before you move",
                "Ask about rent, lease terms, pets, and the details that matter to you.",
              ],
              [
                "03",
                "One place to reach us",
                "Keep our phone and email handy for questions during your tenancy.",
              ],
            ].map(([number, title, body]) => (
              <article key={number}>
                <span>{number}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section id="questions" className="public-section faq-section">
          <div>
            <p className="eyebrow">GOOD TO KNOW</p>
            <h2>
              A few things
              <br />
              you might be wondering.
            </h2>
            <a href="#contact" className="text-link">
              Ask us something else <ArrowUpRight size={17} />
            </a>
          </div>
          <div>
            {faqs.map(([q, a]) => (
              <details key={q}>
                <summary>
                  {q}
                  <ChevronDown size={18} />
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>
        <section id="contact" className="contact-section">
          <div>
            <p className="eyebrow">LET’S FIND YOUR NEXT HOME</p>
            <h2>
              It starts with
              <br />
              <em>a hello.</em>
            </h2>
            <p>
              Tell us a little about what you’re looking for.
              <br />
              We’ll take it from there.
            </p>
            <a href="tel:+12404494338">
              <Phone size={18} /> 240-449-4338
            </a>
            <a href={`mailto:${EMAIL}`}>
              <Mail size={18} /> {EMAIL}
            </a>
            <p className="contact-small">
              Already a resident? Call or email us for help.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setEmailReady(true);
            }}
            className="inquiry-form"
          >
            <h3>Ask about a home</h3>
            <div className="form-pair">
              <label>
                Your name
                <input
                  autoComplete="name"
                  required
                  maxLength={100}
                  value={inquiry.name}
                  onChange={(e) => {
                    setEmailReady(false);
                    setInquiry({ ...inquiry, name: e.target.value });
                  }}
                />
              </label>
              <label>
                Email address
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={inquiry.email}
                  onChange={(e) => {
                    setEmailReady(false);
                    setInquiry({ ...inquiry, email: e.target.value });
                  }}
                />
              </label>
            </div>
            <label>
              Ideal move-in date <span>(optional)</span>
              <input
                type="date"
                value={inquiry.move}
                onChange={(e) => {
                  setEmailReady(false);
                  setInquiry({ ...inquiry, move: e.target.value });
                }}
              />
            </label>
            <label>
              What are you looking for?
              <textarea
                rows={3}
                maxLength={2000}
                required
                placeholder="A home, a neighborhood, or a question…"
                value={inquiry.message}
                onChange={(e) => {
                  setEmailReady(false);
                  setInquiry({ ...inquiry, message: e.target.value });
                }}
              />
            </label>
            <button className="public-button" type="submit">
              Prepare inquiry <ArrowRight size={17} />
            </button>
            <p className="form-note">
              Creates an email draft for you to review and send. Nothing is
              submitted on this website.
            </p>
            {emailReady && (
              <div className="inquiry-ready" role="status">
                <Check size={18} />
                <div>
                  Your inquiry is ready.
                  <a href={draftUrl}>
                    Open your email app to send it <ArrowUpRight size={15} />
                  </a>
                  <span>No email app? Email us directly at {EMAIL}.</span>
                </div>
              </div>
            )}
          </form>
        </section>
      </main>
      <footer className="public-footer">
        <Brand />
        <p>Five homes. A personal approach.</p>
        <div>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
        </div>
        <small>© {new Date().getFullYear()} Highlander Homes LLC</small>
      </footer>
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent>
          <DialogTitle>{selected?.name}</DialogTitle>
          <DialogDescription>{selected?.area}</DialogDescription>
          {selected && (
            <>
              <p>{selected.description}</p>
              <p>
                {selected.bedrooms ? `${selected.bedrooms} bedrooms · ` : ""}
                {selected.bathrooms ? `${selected.bathrooms} bathrooms · ` : ""}
                {formatRent(selected.rent)} / month
              </p>
              <a
                href="#contact"
                className="public-button"
                onClick={() => {
                  setInquiry({
                    ...inquiry,
                    message: `I’m interested in ${selected.name}. Please share availability and tour options.`,
                  });
                  setEmailReady(false);
                  setSelected(null);
                }}
              >
                Ask about this home <ArrowRight size={17} />
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
